import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { InquiryRegulationScope } from "../api/supplierInquiriesApi"
import {
  normalizeFrameworkCode,
  regulationLabel,
  regulationShortLabel,
} from "./regulationLabel"

type Translate = (key: TranslationKeys) => string

/**
 * The canonical framework keys a supplier inquiry can ask about, in catalogue order:
 * a mirror of backend `mc_conformity_frameworks.FRAMEWORK_ORDER`, kept in sync BY
 * HAND (same arrangement as `regulationLabel.ts`). Orders every scope display and
 * offers the list filter's options.
 */
export const INQUIRY_REGULATION_KEYS = [
  "REACH_SVHC",
  "REACH_ANNEX_XIV",
  "REACH_ANNEX_XVII",
  "ROHS",
  "POPS",
  "PFAS",
  "ELV",
  "CHINA_ROHS_2",
  "TSCA",
  "PROP_65",
  "HALOGEN_FREE",
] as const

/** Each regulation's prerequisite keys, a mirror of backend
 *  `mc_conformity_frameworks.FRAMEWORK_DEPENDENCIES` (PFAS is answered against
 *  REACH Annex XVII). */
export const REGULATION_KEY_DEPENDENCIES: Readonly<
  Record<string, readonly string[]>
> = { PFAS: ["REACH_ANNEX_XVII"] }

/**
 * `keys` plus every prerequisite they need (PFAS brings REACH Annex XVII), in
 * catalogue order: the closure the backend applies to a declaration's pick
 * (`with_key_prerequisites`), so a preselection shows what the server will declare.
 */
export function withKeyPrerequisites(keys: readonly string[]): string[] {
  const next = new Set<string>()
  const queue = [...keys]
  while (queue.length > 0) {
    const key = queue.pop()!
    if (next.has(key)) continue
    next.add(key)
    queue.push(...(REGULATION_KEY_DEPENDENCIES[key] ?? []))
  }
  return INQUIRY_REGULATION_KEYS.filter((k) => next.has(k))
}

/**
 * Picks or drops one regulation key the way Settings couples its checkboxes:
 * picking one also picks its prerequisites, dropping one drops every key that
 * needs it. Returns the keys in catalogue order.
 */
export function toggleRegulationKey(
  keys: readonly string[],
  key: string,
): string[] {
  const next = new Set(keys)
  if (next.has(key)) {
    next.delete(key)
    let changed = true
    while (changed) {
      changed = false
      for (const held of [...next]) {
        const requires = REGULATION_KEY_DEPENDENCIES[held] ?? []
        if (requires.some((dep) => !next.has(dep))) {
          next.delete(held)
          changed = true
        }
      }
    }
  } else {
    const queue = [key]
    while (queue.length > 0) {
      const picked = queue.pop()!
      if (next.has(picked)) continue
      next.add(picked)
      queue.push(...(REGULATION_KEY_DEPENDENCIES[picked] ?? []))
    }
  }
  return INQUIRY_REGULATION_KEYS.filter((k) => next.has(k))
}

/** The regions the inquiry picker groups its regulations by, like Settings ›
 *  Regelwerke (`regionOf` there reads each catalogue row's jurisdiction; this is
 *  the same split for the keys, kept in sync by hand). */
export type RegulationRegion = "eu" | "cn" | "us" | "other"

export const REGULATION_REGION_ORDER: RegulationRegion[] = [
  "eu",
  "cn",
  "us",
  "other",
]

const REGION_BY_KEY: Readonly<Record<string, RegulationRegion>> = {
  REACH_SVHC: "eu",
  REACH_ANNEX_XIV: "eu",
  REACH_ANNEX_XVII: "eu",
  ROHS: "eu",
  POPS: "eu",
  PFAS: "eu",
  ELV: "eu",
  CHINA_ROHS_2: "cn",
  TSCA: "us",
  PROP_65: "us",
}

export function regulationRegion(key: string): RegulationRegion {
  return REGION_BY_KEY[key] ?? "other"
}

/** The inquiry key for any framework spelling the app carries (a CMS slug such as
 *  "reach-svhc", a code, a key), or null when it names none: Studio's "Anfordern"
 *  hands over the declaration-side slug of the regulation it came from. */
export function regulationKeyFor(
  raw: string | null | undefined,
): string | null {
  const code = normalizeFrameworkCode(raw ?? "")
  if (!code) return null
  return (
    INQUIRY_REGULATION_KEYS.find(
      (key) => normalizeFrameworkCode(key) === code,
    ) ?? null
  )
}

/** A pick with PFAS but without REACH Annex XVII: allowed on an inquiry, but a PFAS
 *  conformity letter later needs Annex XVII, so the picker says so (decision N11). */
export function picksPfasWithoutAnnexXvii(keys: readonly string[]): boolean {
  return keys.includes("PFAS") && !keys.includes("REACH_ANNEX_XVII")
}

/**
 * The keys the list's Regelwerk filter offers: those some inquiry format can ask about,
 * a mirror of backend `mc_inquiry_regulation_scope.ASKABLE_REGULATIONS` (ELV has a
 * section in no document; the backend 422s on it).
 */
export const INQUIRY_FILTER_KEYS = INQUIRY_REGULATION_KEYS.filter(
  (key) => key !== "ELV",
)

/**
 * The regulations an inquiry's scope names that its format has no section for (Annex
 * XIV/XVII in the PDF + Excel package), in the order given. Empty while what the
 * documents ask is unknown: without it nothing can be said to be missing. Empty too
 * when they ask nothing: a draft whose format covers none of the scope shows
 * `regulationScopeError` instead (and cannot be sent), and an inquiry that asked
 * nothing hides its scope, so listing the whole scope here would only repeat that.
 */
export function notAskedRegulations(
  askedRegulations: readonly string[] | null | undefined,
  regulationKeys: readonly string[] | null | undefined,
): string[] {
  if (!askedRegulations?.length) return []
  const asked = new Set(askedRegulations)
  return (regulationKeys ?? []).filter((key) => !asked.has(key))
}

/** PFAS is asked against POPs and the PFAS entries of REACH Annex XVII: when the
 *  documents ask PFAS but not the scope's Annex XVII, the rest of XVII (lead, nickel,
 *  PAHs, phthalates, …) goes unasked, and the note says what PFAS still covers. */
export function pfasLacksAnnexXvii(
  askedRegulations: readonly string[] | null | undefined,
  notAsked: readonly string[],
): boolean {
  return (
    !!askedRegulations?.includes("PFAS") &&
    notAsked.includes("REACH_ANNEX_XVII")
  )
}

/** One regulation of an inquiry's scope, labelled for a pill. */
export interface InquiryRegulationLabel {
  key: string
  /** Short badge text (SVHC, RoHS, PFAS…). */
  label: string
  /** The full name, for a tooltip. */
  title: string
}

/** How a scope shows: its regulations, the org default a draft follows, the full
 *  documents of an inquiry sent before per-inquiry scopes, or nothing at all. */
export type InquiryScopeView =
  | { kind: "regulations"; regulations: InquiryRegulationLabel[] }
  | { kind: "orgDefault" }
  | { kind: "all" }
  | { kind: "none" }

/** Canonical keys -> labelled regulations in catalogue order. A key this list does
 *  not know (newer backend) sorts last and shows as the key itself. */
export function inquiryRegulationLabels(
  keys: readonly string[],
  translate: Translate,
): InquiryRegulationLabel[] {
  const order = (key: string) => {
    const index = (INQUIRY_REGULATION_KEYS as readonly string[]).indexOf(key)
    return index === -1 ? INQUIRY_REGULATION_KEYS.length : index
  }
  return [...keys]
    .sort((a, b) => order(a) - order(b))
    .map((key) => {
      const code = normalizeFrameworkCode(key)
      return {
        key,
        label: regulationShortLabel(code, key, translate),
        title: regulationLabel(code, key, translate),
      }
    })
}

/**
 * The one decision every inquiry surface (list row, card, detail, timeline) shows a
 * scope by. What the documents actually ask (`askedRegulations`) wins, `[]` meaning
 * none; when that is unknown, the stored keys; without them the server's
 * `regulationScope` says why: a draft following Settings, a pre-epic inquiry that
 * asked everything, or none.
 */
export function inquiryScopeView(
  inquiry: {
    askedRegulations?: readonly string[] | null
    regulationKeys?: readonly string[] | null
    regulationScope?: InquiryRegulationScope | null
  },
  translate: Translate,
): InquiryScopeView {
  if (inquiry.askedRegulations != null && inquiry.askedRegulations.length === 0)
    return { kind: "none" }
  const keys = inquiry.askedRegulations ?? inquiry.regulationKeys
  if (keys && keys.length > 0) {
    return {
      kind: "regulations",
      regulations: inquiryRegulationLabels(keys, translate),
    }
  }
  if (inquiry.regulationScope === "org_default") return { kind: "orgDefault" }
  if (inquiry.regulationScope === "all") return { kind: "all" }
  return { kind: "none" }
}

/** `inquiryScopeView` as one line of text, for surfaces without pills (the
 *  active-work card, the timeline); null when the inquiry asked no regulation. */
export function inquiryScopeText(
  inquiry: Parameters<typeof inquiryScopeView>[0],
  translate: Translate,
): string | null {
  const view = inquiryScopeView(inquiry, translate)
  switch (view.kind) {
    case "regulations":
      return view.regulations.map((r) => r.label).join(", ")
    case "orgDefault":
      return translate("MC_INQ_SCOPE_ORG_DEFAULT")
    case "all":
      return translate("MC_INQ_SCOPE_ALL")
    case "none":
      return null
  }
}
